export const PATTERN_SIZE_TOKENS = Object.freeze({
  xsmall: 14, small: 18, medium: 26, large: 34, xlarge: 39,
});

export const PATTERN_SIZE_READABILITY = Object.freeze({
  minimumGlyphPx: 14,
  mobileCap: 1,
  largeDisplayCap: 1.15,
  largeDisplayWidth: 700,
  overflowBufferPx: 4,
  normalPaddingPx: 6,
  normalGapPx: 4,
  compactPaddingPx: 2,
  compactGapPx: 2,
  connectorPx: 16,
  maxColumns: 8,
});

// Derive legacy policy from visual data, never from instruction/label text.
export function patternSizePolicy(question) {
  const sizes = [];
  const issues = new Set();
  let inferred = false;
  const add = (size, semantic = true) => {
    if (!Object.prototype.hasOwnProperty.call(PATTERN_SIZE_TOKENS, size)) {
      issues.add(`unknown_size_token:${String(size)}`);
      return;
    }
    sizes.push(size);
    inferred ||= semantic;
  };
  const visit = (value) => {
    if (!value || typeof value !== 'object') return;
    if (value.kind === 'size_symbol') add(value.visual_size || 'medium');
    if (value.kind === 'library_symbol' && value.visual_size) add(value.visual_size);
    if (value.kind === 'matrix_token' && value.size) add(value.size, value.size !== 'medium');
    if (value.kind === 'size_sequence') (value.sizes || []).forEach((size) => add(size));
    if (value.kind === 'dual_rhythm') (value.sizes || []).forEach((size) => add(size));
    if (value.kind === 'attribute_sequence') {
      (value.items || []).forEach((item) => { if (item.size) add(item.size); });
    }
    Object.values(value).forEach((child) => {
      if (Array.isArray(child)) child.forEach(visit);
      else if (child && typeof child === 'object') visit(child);
    });
  };
  visit(question?.prompt);
  const referenceHasSize = inferred;
  (question?.choices || []).forEach((choice) => visit(choice.value));
  const explicit = question?.visual_policy?.size_semantics ?? question?.visual_spec?.size_semantics;
  if (explicit && !['intrinsic', 'decorative'].includes(explicit)) issues.add('unknown_size_semantics');
  if (!referenceHasSize && inferred && ['size', 'shape_size'].includes(question?.representation)) {
    issues.add('reference_size_not_structured');
  }
  const enabled = explicit === 'decorative' ? false : explicit === 'intrinsic' || inferred;
  const review = issues.size > 0;
  const dimensions = sizes.map((size) => PATTERN_SIZE_TOKENS[size]);
  return {
    enabled: enabled && !review,
    semantics: review ? 'review' : enabled ? 'intrinsic' : 'decorative',
    issues: [...issues],
    maximumSize: Math.max(PATTERN_SIZE_TOKENS.medium, ...dimensions),
    minimumSize: Math.min(...(dimensions.length ? dimensions : [PATTERN_SIZE_TOKENS.medium])),
  };
}

export function fitPatternQuestionScale(rows, policy, cap, tokens = PATTERN_SIZE_READABILITY) {
  const measured = rows.filter((row) => row.width > tokens.overflowBufferPx && row.count > 0);
  const floor = Math.min(cap, tokens.minimumGlyphPx / policy.minimumSize);
  const fit = (padding, gap) => Math.min(cap, ...measured.map((row) => (
    (row.width - tokens.overflowBufferPx)
      / (row.count * (policy.maximumSize + padding)
        + row.connectors * tokens.connectorPx + Math.max(0, row.count - 1) * gap)
  )));
  const normal = fit(tokens.normalPaddingPx, tokens.normalGapPx);
  if (normal >= floor) return { scale: normal, padding: tokens.normalPaddingPx, gap: tokens.normalGapPx, wrap: false };
  const compact = fit(tokens.compactPaddingPx, tokens.compactGapPx);
  if (compact >= floor) return { scale: compact, padding: tokens.compactPaddingPx, gap: tokens.compactGapPx, wrap: false };
  return { scale: floor, padding: tokens.compactPaddingPx, gap: tokens.compactGapPx, wrap: true };
}
